import { CalendarPlus, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { GestorJornadaRegisterForm } from '@/components/Gestores/GestorJornadaRegisterForm'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import {
  jornadaCadenciaLabel,
  jornadaPrazoClass,
  jornadaPrazoLabel,
  jornadaProximaLabel,
  jornadaRowToneClass,
  jornadaSetorLabel,
  jornadaStatusBadge,
  jornadaUltimaLabel,
} from '@/components/Gestores/gestorJornadaMembroUi'
import type { JornadaCadenciaDias } from '@/components/Gestores/gestorJornadaUi'
import type { GestorJornadaMembro } from '@/services/gestoresService'
import { cn } from '@/utils/cn'
import { Fragment } from 'react'

type Props = {
  membros: GestorJornadaMembro[]
  expandedId: string | null
  registrandoId: string | null
  onToggleRegister: (id: string) => void
  onOpenFeedback: (id: string) => void
  onCadenciaChange: (pjId: string, cadencia: JornadaCadenciaDias) => Promise<void>
  onRegister: (pjId: string, body: { realizadaEm: string; notas?: string }) => Promise<void>
}

export function GestorJornadaMembrosTable({
  membros,
  expandedId,
  registrandoId,
  onToggleRegister,
  onOpenFeedback,
  onCadenciaChange,
  onRegister,
}: Props) {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full min-w-[880px]'>
        <thead className='sticky top-0 z-10 bg-[var(--glass-bg)] backdrop-blur-sm'>
          <tr className='border-b border-[var(--glass-border)]'>
            {['Profissional', 'Setor', 'Ciclo', 'Última 1:1', 'Próxima 1:1', 'Status', 'Ações'].map(
              (h) => (
                <th key={h} className={GESTOR_TABLE_TH_CLASS} style={{ fontSize: 'var(--font-size-label)' }}>
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {membros.map((m) => {
            const status = jornadaStatusBadge(m.situacao)
            const expanded = expandedId === m.id

            return (
              <Fragment key={m.id}>
                <tr className={cn(GESTOR_TABLE_TR_CLASS, 'cursor-default', jornadaRowToneClass(m.situacao))}>
                  <td className='px-4 py-3'>
                    <div className='flex items-center gap-3'>
                      <GestorTableAvatar name={m.nomeCompleto} />
                      <div className='min-w-0'>
                        <p className='truncate font-medium text-text-primary' style={{ fontSize: 'var(--font-size-sm)' }}>
                          {m.nomeCompleto}
                        </p>
                        {m.feedbackResumo.feedbackRascunho > 0 && (
                          <p className='text-[10px] text-amber-400'>
                            {m.feedbackResumo.feedbackRascunho} rascunho(s) FB
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3 text-sm text-text-muted'>{jornadaSetorLabel(m.setor)}</td>
                  <td className='px-4 py-3 text-sm text-text-muted' title='Intervalo configurado entre reuniões 1:1'>
                    {jornadaCadenciaLabel(m.cadenciaDias)}
                  </td>
                  <td className='px-4 py-3 text-sm text-text-primary'>
                    {m.ultimaOneOnOneEm ? (
                      jornadaUltimaLabel(m.ultimaOneOnOneEm)
                    ) : (
                      <span className='text-text-muted'>Nunca registrada</span>
                    )}
                  </td>
                  <td className='px-4 py-3 text-sm'>
                    <span className='text-text-primary'>{jornadaProximaLabel(m.proximaOneOnOneEm)}</span>
                    <span className={cn('ml-1 text-xs', jornadaPrazoClass(m.proximaOneOnOneEm))}>
                      ({jornadaPrazoLabel(m.proximaOneOnOneEm)})
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                        status.className,
                      )}
                    >
                      {status.label}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <div className='flex items-center gap-1'>
                      <Button
                        type='button'
                        size='sm'
                        variant={expanded ? 'primary' : 'secondary'}
                        onClick={() => onToggleRegister(m.id)}
                        title='Registrar 1:1'
                      >
                        <CalendarPlus size={14} />
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => onOpenFeedback(m.id)}
                        title='Feedback'
                      >
                        <MessageSquare size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
                {expanded && (
                  <tr className='border-b border-[var(--glass-border)]'>
                    <td colSpan={7} className='p-0'>
                      <GestorJornadaRegisterForm
                        loading={registrandoId === m.id}
                        cadenciaDias={m.cadenciaDias}
                        onCadenciaChange={(c) => void onCadenciaChange(m.id, c)}
                        onCancel={() => onToggleRegister(m.id)}
                        onSubmit={async (body) => {
                          try {
                            await onRegister(m.id, body)
                            onToggleRegister(m.id)
                          } catch {
                            // mantém aberto
                          }
                        }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
